import React, { useContext, useEffect, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as GoogleIcon } from '@pkg/assets/icons/v2/google.svg';
import { ReactComponent as MailIcon } from '@pkg/assets/icons/v2/mail.svg';
import { ReactComponent as AppleIcon } from '@pkg/assets/icons/v2/pay_apple.svg';
import { ReactComponent as DiscordIcon } from '@pkg/assets/icons/v2/social/discord.svg';
import { ReactComponent as TwitterIcon } from '@pkg/assets/icons/v2/social/twitter.svg';
import GemstoneUrl from '@pkg/assets/images/v2/emoji/gemstone.png';
import { RedirectSingleton, RedirectType } from '@pkg/classes';
import { useWeb3RenderContext, Web3AuthContext, Web3AuthPanelRenderContext } from '@pkg/components';
import { DepButton, DepButtonDisplay, DepInput, DepInputSize, DepPassword } from '@pkg/components/deprecated';
import { useAppDispatch } from '@pkg/hooks/redux';
import { TwoFactorAuthType } from '@pkg/types';

import { callLogin, callProviderAuth } from '@app/client/resources/w3auth';
import { AuthProviders } from '@app/client/resources/w3auth/types';
import config from '@app/config';
import { loginWithToken, setAuthTokens } from '@app/store/actions/auth';

import { Typography } from '../DesignSystem';

import {
    ButtonIcon,
    ButtonItem,
    ButtonList,
    Container,
    ContentLegend,
    EmojiImage,
    EmphasizedTypography,
    ErrorMessage,
    FormGroup,
    FormRow,
    InternalLink,
    PrimaryCopySection,
    Section,
} from './styles';

export function EntryPoint() {
    const { authProviders } = config;
    const [translate] = useTranslation(['auth', 'common', 'fantasy_wishlist']);

    const dispatch = useAppDispatch();
    const { setCurrentStep } = useContext(Web3AuthContext);
    const { renderCtx } = useWeb3RenderContext();

    const location = useLocation();

    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const [hasAuthError, setHasAuthError] = useState(false);

    const teamContext = renderCtx === Web3AuthPanelRenderContext.Teams;

    async function handleAuthProviderClick(provider: AuthProviders) {
        if (provider === AuthProviders.Twitter) {
            setCurrentStep('add-twitter');
        } else {
            const link = await callProviderAuth(provider);
            if (link) {
                RedirectSingleton.setRedirect(RedirectType.Default, location.pathname);
                window.location.href = link;
            }
        }
    }

    function handleEmailClick() {
        setCurrentStep('sign-up-with-email');
    }

    function handleForgotClick() {
        setCurrentStep('forgot-password');
    }

    async function handleEmailLogin() {
        setLoading(true);
        try {
            const token = await callLogin({ email, password });
            const twoFAShouldPrompt = Boolean(token?.requires_2fa && token?.requires_2fa !== TwoFactorAuthType.Invalid);

            if (twoFAShouldPrompt) {
                dispatch(setAuthTokens(token));
            } else {
                dispatch(loginWithToken(token));
            }
            setLoading(false);
            const postLoginRedirect = RedirectSingleton.getRedirect(RedirectType.PostLogin) || '/';
            if (location.pathname === '/auth/login') {
                navigate(postLoginRedirect);
                RedirectSingleton.removeRedirect(RedirectType.PostLogin);
            }
        } catch (error) {
            setHasAuthError(true);
            setLoading(false);
        }
    }

    useEffect(() => {
        if (hasAuthError) {
            setHasAuthError(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, password]);

    return (
        <Container>
            <Section>
                <EmojiImage src={GemstoneUrl} />
            </Section>

            <PrimaryCopySection>
                <Typography type="headline" size={40}>
                    {teamContext
                        ? translate('w3a_panel_join_team_header', { ns: 'fantasy_wishlist' })
                        : translate('entry_point_heading')}
                </Typography>

                <Typography type="paragraph" size={18} faded={true}>
                    {translate('entry_point_subheading')}
                </Typography>
            </PrimaryCopySection>

            <Section>
                <ButtonList>
                    {authProviders.google && (
                        <ButtonItem>
                            <DepButton
                                variant="muted"
                                display={DepButtonDisplay.block}
                                onClick={() => handleAuthProviderClick(AuthProviders.Google)}
                            >
                                <ButtonIcon>
                                    <GoogleIcon />
                                </ButtonIcon>

                                <span>{translate('common:google')}</span>
                            </DepButton>
                        </ButtonItem>
                    )}

                    {authProviders.twitter && (
                        <ButtonItem>
                            <DepButton
                                variant="muted"
                                display={DepButtonDisplay.block}
                                onClick={() => handleAuthProviderClick(AuthProviders.Twitter)}
                            >
                                <ButtonIcon>
                                    <TwitterIcon style={{ fill: 'hsla(204, 88%, 53%, 1)' }} />
                                </ButtonIcon>

                                {translate('common:twitter')}
                            </DepButton>
                        </ButtonItem>
                    )}

                    {authProviders.apple && (
                        <ButtonItem>
                            <DepButton
                                variant="muted"
                                display={DepButtonDisplay.block}
                                onClick={() => handleAuthProviderClick(AuthProviders.Apple)}
                            >
                                <ButtonIcon>
                                    <AppleIcon />
                                </ButtonIcon>

                                {translate('common:apple')}
                            </DepButton>
                        </ButtonItem>
                    )}

                    {authProviders.discord && (
                        <ButtonItem>
                            <DepButton
                                variant="muted"
                                display={DepButtonDisplay.block}
                                onClick={() => handleAuthProviderClick(AuthProviders.Discord)}
                            >
                                <ButtonIcon>
                                    <DiscordIcon style={{ fill: 'hsla(235, 86%, 65%, 1)' }} />
                                </ButtonIcon>

                                {translate('common:discord')}
                            </DepButton>
                        </ButtonItem>
                    )}

                    <ButtonItem>
                        <DepButton variant="muted" display={DepButtonDisplay.block} onClick={handleEmailClick}>
                            <ButtonIcon>
                                <MailIcon />
                            </ButtonIcon>

                            {translate('common:email')}
                        </DepButton>
                    </ButtonItem>
                </ButtonList>

                <ContentLegend>
                    <Typography type="paragraph" size={16} color="rgba(255,255,255,0.5)" weight="700">
                        <Trans ns="auth" i18nKey="or_log_in_with_email">
                            <EmphasizedTypography>0</EmphasizedTypography>
                        </Trans>
                    </Typography>
                </ContentLegend>

                <FormGroup onSubmit={handleEmailLogin}>
                    <FormRow>
                        <DepInput
                            autoFocus
                            value={email}
                            label={translate('common:email')}
                            sizeVariant={DepInputSize.Compact}
                            onChange={({ target }) => setEmail(target.value)}
                        />
                    </FormRow>

                    <FormRow>
                        <DepPassword
                            type="password"
                            value={password}
                            label={translate('common:password')}
                            sizeVariant={DepInputSize.Compact}
                            autoFocus={false}
                            onChange={({ target }) => setPassword(target.value)}
                            onKeyDown={({ key }) => {
                                if (key === 'Enter') handleEmailLogin();
                            }}
                        />

                        <DepButton
                            type="submit"
                            variant="glow"
                            disabled={!(email && password)}
                            onClick={handleEmailLogin}
                            loading={loading}
                        >
                            {translate('email_login_submit')}
                        </DepButton>
                    </FormRow>

                    <FormRow>
                        <InternalLink href="#forgot-password" onClick={handleForgotClick}>
                            {translate('forgot_password_action')}
                        </InternalLink>
                    </FormRow>
                </FormGroup>

                <FormGroup>
                    <ErrorMessage>{hasAuthError && `Invalid email or password.`}</ErrorMessage>
                </FormGroup>
            </Section>
        </Container>
    );
}
